import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { Layout } from '@components/layout/Layout'
import { HomePage } from '@pages/HomePage'
import { WorkshopPage } from '@pages/WorkshopPage'
import { WorkshopManagementPage } from '@pages/WorkshopManagementPage'
import { RevisionRemindersPage } from '@pages/RevisionRemindersPage'
import { CatalogPage } from '@pages/CatalogPage'
import { WorkOrdersPage } from '@pages/WorkOrdersPage'
import { MechanicWorkOrdersPage } from '@pages/MechanicWorkOrdersPage'
import { MechanicClientsPage } from '@pages/MechanicClientsPage'
import { WorkOrderDetailPage } from '@pages/WorkOrderDetailPage'
import { ProfilePage } from '@pages/ProfilePage'
import { NotFoundPage } from '@pages/NotFoundPage'
import { LoginPage } from '@pages/LoginPage'
import { PublicLandingPage } from '@pages/PublicLandingPage'
import { useAuthStore } from '@store/useAuthStore'
import { ApplicationAccessGate } from '@components/auth/ApplicationAccessGate'
import { RoleProtectedRoute } from '@components/auth/RoleProtectedRoute'
import { SubscriptionRequiredRoute } from '@components/auth/SubscriptionRequiredRoute'
import { ClientModalProvider } from '@contexts/ClientModalContext'

function ProtectedLayout() {
  const { isAuthenticated, checkTokenExpiry } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const isTokenValid = checkTokenExpiry()
  if (!isTokenValid) {
    return <Navigate to="/login" replace />
  }

  return (
    <ClientModalProvider>
      <Layout>
        <Outlet />
      </Layout>
    </ClientModalProvider>
  )
}

/**
 * Homepage (autenticada): chat da receção. Acessível a rececionista e administrador.
 * Mecânico é redirecionado para folhas de obra (não usa esta página).
 * Não confundir com a landing pública em `/`.
 */
function HomepageEntry() {
  const { role, getRole } = useAuthStore()
  const effectiveRole = role ?? (typeof getRole === 'function' ? getRole() : null)
  if (effectiveRole === 'mechanic') {
    return <Navigate to="/work-orders" replace />
  }
  return <HomePage />
}

/** Landing page: `/` só com marketing (sem sessão). Com sessão, redireciona para a homepage da app. */
function RootPage() {
  const { isAuthenticated, checkTokenExpiry, hasApplicationAccess } = useAuthStore()
  const tokenOk = isAuthenticated && checkTokenExpiry()

  if (!tokenOk) {
    return <PublicLandingPage />
  }

  if (hasApplicationAccess === false) {
    return <Navigate to="/subscription-required" replace />
  }

  return <Navigate to="/homepage" replace />
}

/**
 * Rotas públicas (sem sessão): apenas `/` (landing) e `/login`.
 * Homepage da aplicação (com chat): `/homepage` dentro do layout autenticado.
 */
function App() {
  return (
    <Routes>
      <Route path="/" element={<RootPage />} />
      <Route path="/welcome" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/subscription-required" element={<SubscriptionRequiredRoute />} />

      <Route element={<ProtectedLayout />}>
        <Route element={<ApplicationAccessGate />}>
        <Route path="/home" element={<Navigate to="/homepage" replace />} />
        <Route path="/homepage" element={<HomepageEntry />} />
        <Route
          path="/mechanic/work-orders"
          element={
            <RoleProtectedRoute allowedRoles={['mechanic', 'administrator']}>
              <MechanicWorkOrdersPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/mechanic/clients"
          element={
            <RoleProtectedRoute allowedRoles={['mechanic', 'administrator']}>
              <MechanicClientsPage />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/catalog"
          element={
            <RoleProtectedRoute allowedRoles={['receptionist', 'mechanic', 'administrator']}>
              <CatalogPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/work-orders"
          element={
            <RoleProtectedRoute allowedRoles={['receptionist', 'mechanic', 'administrator']}>
              <WorkOrdersPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/work-orders/:id"
          element={
            <RoleProtectedRoute allowedRoles={['receptionist', 'mechanic', 'administrator']}>
              <WorkOrderDetailPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <RoleProtectedRoute allowedRoles={['receptionist', 'administrator']}>
              <MechanicClientsPage />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/revisions"
          element={
            <RoleProtectedRoute allowedRoles={['administrator', 'receptionist', 'mechanic']}>
              <RevisionRemindersPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/workshop"
          element={
            <RoleProtectedRoute allowedRoles={['administrator', 'receptionist']}>
              <WorkshopPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/workshop/management"
          element={
            <RoleProtectedRoute allowedRoles={['administrator']}>
              <WorkshopManagementPage />
            </RoleProtectedRoute>
          }
        />
        <Route path="/profile" element={<ProfilePage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
